/// <amd-module name="Search/Hub/Components/SearchHub" />
import * as React from "react";
import * as VssUiPage from "VSSUI/Page";
import * as VssUiTabs from "VSSUI/Tabs";
import * as Resources from "Search/Shared/Resources";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ObservableValue, IReadonlyObservableArray, IObservableValue } from "VSS/Core/Observable";
import { SearchInput } from "Search/Shared/Components/SearchInput";
import { css, VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { ITfsPageData, getTFSPageData } from "Tfs/Platform/Page";
import { ITfsSearchQuery } from "Tfs/Platform/Search";
import { ITabContext, IInputQuery, IInputLabel, PivotResults } from "Search/Shared/Base/Types";
import { INotificationProps, NotificationArea } from "Search/Shared/Components/NotificationArea";
import { MessageBarSeverity } from "VSSUI/MessageBar";
import { IPivotCommands, PivotCommands } from "Search/Shared/Components/PivotCommands";
import { IVssHistoryService, HistoryServiceEventNames, INavigationStateChangedEvent } from "VSS/Platform/History";
import { IVssSecurityService } from "VSS/Platform/Security";
import { IVssSearchService, ISearchStatus } from "VSS/Platform/Search";
import { ISearchHubState, IVssContributedSearchTab } from "Search/Hub/Components/SearchHub.types";
import { HubTelemetryWriter } from "Search/Shared/Base/Scenario";
import { SearchHubRouteConstants } from "Search/Shared/Constants";
import { IVssPageService } from "VSS/Platform/Page";
import { SecurityConstants, TelemetryConstants } from "Search/Hub/Constants";
import { ResultsCountSource } from "Search/Shared/Base/Sources/ResultsCountSource";
import { ExtendedSearchSection } from "Search/Shared/Components/ExtendedSearchSection";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { ISearchPivotStateService } from "Search/Shared/Base/PivotStateService";
import { RelationFromExactCount } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import { IVssLayoutManager } from "VSS/Platform/Layout";

const HubTabSwitchScenarioName = "SearchHubTabSwitchScenario";

export class SearchHub extends VssComponent<IVssComponentProperties, ISearchHubState> {
    private readonly pageData: ITfsPageData | undefined;
    private readonly inputQuery: ObservableValue<IInputQuery>;
    private readonly inputLabel: ObservableValue<IInputLabel>;
    private readonly defaultSearchText: ObservableValue<string | undefined>;
    private readonly pageNotification: ObservableValue<INotificationProps>;
    private readonly pivotCommands: ObservableValue<IPivotCommands>;
    private readonly telemetryWriter: HubTelemetryWriter;
    private readonly isHosted: boolean;
    private readonly isMember: boolean;
    private readonly source: ResultsCountSource;

    private readonly pivotResultsCount: PivotResults;
    private readonly historyService: IVssHistoryService;
    private readonly searchService: IVssSearchService;
    private readonly pivotStateService: ISearchPivotStateService;

    private tabProviders: IReadonlyObservableArray<IVssContributedSearchTab>;
    private selectedTab: IObservableValue<string>;
    private disposeActions: (() => void)[];

    constructor(props: never, context: IVssComponentContext) {
        super(props, context);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeParams: { [key: string]: string } = this.historyService.getState();

        this.selectedTab = new ObservableValue<string>(routeParams.type);
        this.telemetryWriter = new HubTelemetryWriter(HubTabSwitchScenarioName, context.pageContext);
        this.pageNotification = new ObservableValue<INotificationProps>({
            visible: false,
            message: "",
            isFormatted: false,
            severity: MessageBarSeverity.Info
        });
        this.inputQuery = new ObservableValue<IInputQuery>({});
        this.defaultSearchText = new ObservableValue<string>(routeParams.text);
        this.inputLabel = new ObservableValue<IInputLabel>({
            inputPlaceholder: "",
            inputAriaLabel: ""
        });
        this.pivotCommands = new ObservableValue<IPivotCommands>({ farItems: [], items: [] });
        this.pageData = getTFSPageData(this.context.pageContext);
        this.isHosted = context.pageContext.getService<IVssPageService>("IVssPageService").getData()!.isHosted;
        this.pivotResultsCount = {};
        this.source = new ResultsCountSource(this.context.pageContext);

        this.isMember = this.isUserMember();
        this.state = {
            isTabSwitch: false,
            isFullScreen: this.context.pageContext
                .getService<IVssLayoutManager>("IVssLayoutManager")
                .getFullScreenMode()
                .value
        };
        this.disposeActions = [];
        this.searchService = context.pageContext.getService<IVssSearchService>("IVssSearchService");
        this.pivotStateService = context.pageContext.getService<ISearchPivotStateService>("ISearchPivotStateService");
    }

    public componentDidMount(): void {
        // subscribe to changes in default search text be it either upon user entering search text or on updates upon history navigation.
        this.defaultSearchText.subscribe(this.onDefaultSearchTextModified);
        this.selectedTab.subscribe(this.onSelectedPivotChanged);
        // subscribe to history for changes in "text" url param upon history navigation.
        this.historyService.subscribe(this.onNavigate, HistoryServiceEventNames.statePopped);

        // update count badges on pivots.
        this.updatePivotResultsCount(this.selectedTab.value, this.defaultSearchText.value);
        this.searchService.subscribe(this.onInPageSearchStarted, "searchStarted");

        this.disposeActions.push(() => this.defaultSearchText.unsubscribe(this.updatePivotResultsCount));
        this.disposeActions.push(() => this.selectedTab.unsubscribe(this.onSelectedPivotChanged));
        this.disposeActions.push(() => this.historyService.unsubscribe(this.onNavigate));
        this.disposeActions.push(() => this.searchService.unsubscribe(this.onInPageSearchStarted));
    }

    public componentWillUnmount(): void {
        this.disposeActions.forEach(action => action());
        this.tabProviders.value.forEach(tab => {
            this.pivotResultsCount[tab.id].unsubscribe(() => this.updateTabBadgeCount(tab));
        });
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return (
            <VssUiPage.Page className="search-results-page flex-grow" tabProviders={this.getTabProviders()} selectedTabId={this.selectedTab}>
                <NotificationArea notification={this.pageNotification} />
                <div className={css("search-input-container flex-row flex-noshrink", this.state.isFullScreen && "hidden")}>
                    <SearchInput
                        pageContext={this.context.pageContext}
                        selectedTabId={this.selectedTab}
                        defaultText={this.defaultSearchText}
                        inputLabel={this.inputLabel}
                        onExecuteSearch={(text: string, openInNewTab: boolean) => {
                            this.inputQuery.value = { text, openInNewTab };
                        }}
                        onFocus={this.onInputFocus}
                        contextLabel={this.getContextLabel()} />
                    <ExtendedSearchSection
                        className="extended-search-section"
                        pageContext={this.context.pageContext}
                        selectedTabId={this.selectedTab}
                        defaultSearchText={this.defaultSearchText}
                        project={this.pageData && this.pageData.project} />
                </div>
                <VssUiTabs.TabBar
                    className={css("search-providers-tabBar", this.state.isFullScreen && "hidden")}
                    tabsClassName="disable-flex-wrap-for-search-page"
                    disableSticky={true}
                    tabSize={VssUiTabs.TabSize.Tall}
                    onSelectedTabChanged={this.onTabChanged}
                    renderAdditionalContent={
                        () => <PivotCommands pivotCommands={this.pivotCommands} />
                    } />
                <VssUiTabs.TabContent />
            </VssUiPage.Page>
        );
    }

    private getTabProviders(): IReadonlyObservableArray<VssUiTabs.IVssContributedTab> {
        this.tabProviders = getTabs(
            this.context.pageContext,
            "ms.vss-search-web.search-hub:content:pivots",
            {
                props: this.getTabContext()
            });

        this.tabProviders.value.forEach(tab => {
            if (!this.pivotResultsCount[tab.id]) {
                this.pivotResultsCount[tab.id] = new ObservableValue<{ count: number, relationFromExactCount: RelationFromExactCount }>
                    ({ count: 0, relationFromExactCount: RelationFromExactCount.Equals });

                tab.renderBadge = () => <VssUiTabs.TabBadge>{this.getBadgeString(tab.id)}</VssUiTabs.TabBadge>;
                tab.badgeCount = new ObservableValue<number>(0);

                this.pivotResultsCount[tab.id].subscribe(() => this.updateTabBadgeCount(tab));
            }
        });

        return this.tabProviders;
    }

    private updateTabBadgeCount = (tab: IVssContributedSearchTab): void => {
        (tab.badgeCount as ObservableValue<number>).value = this.pivotResultsCount[tab.id].value.count;
    }

    private getBadgeString = (tabId: string): string => {
        if (this.pivotResultsCount[tabId].value.relationFromExactCount !== RelationFromExactCount.LessThanEqualTo) {
            return getFriendlyDisplayValue(this.pivotResultsCount[tabId].value.count);
        } else {
            return this.pivotResultsCount[tabId].value.count + "+";
        }
    }

    private onNavigate = (navigationState: INavigationStateChangedEvent): void => {
        // on history pop events populate default text with the text in the current url.
        const defaultText = this.defaultSearchText.value;
        const { newState } = navigationState;
        if (newState) {
            if (newState.state.text && newState.state.text !== defaultText) {
                this.defaultSearchText.value = newState.state.text;
            }

            if (newState.state.type && newState.state.type !== this.selectedTab.value) {
                this.selectedTab.value = newState.state.type;
            }
        }
    }

    private onTabChanged = (newTabId: string, searchText?: string): void => {
        // Dispose previous notifications if any.
        const notification = this.pageNotification.value;
        const queryText = searchText || this.defaultSearchText.value || "";
        this.pageNotification.value = { ...notification, visible: false };

        this.resetViewOptions(newTabId, queryText);
        this.selectedTab.value = newTabId;

        if (this.defaultSearchText.value !== queryText) {
            // Update default search text and refresh pivot counts as active tab has changed.
            this.defaultSearchText.value = queryText;
        }
    }

    private onSelectedPivotChanged = (newTabId: string): void => {
        this.telemetryWriter.startTabSwitch(newTabId);
        this.setState({ isTabSwitch: true });
    }

    private getContextLabel = (): string => {
        return this.pageData && this.pageData.project ? Resources.LabelForProjectContext : Resources.LabelForAccountContext;
    }

    private getTabContext = (): ITabContext => {
        return {
            pageNotification: this.pageNotification,
            pivotCommands: this.pivotCommands,
            inputQuery: this.inputQuery,
            inputLabel: this.inputLabel,
            telemetryMarker: this.telemetryWriter,
            isTabSwitch: () => this.state.isTabSwitch,
            onToggleFullScreen: this.onToggleFullScreen,
            isFullScreen: () => this.state.isFullScreen,
            isHosted: this.isHosted,
            isMember: this.isMember,
            resultsCount: this.pivotResultsCount
        }
    }

    private onToggleFullScreen = (isFullScreen: boolean): void => {
        this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").setFullScreenMode(isFullScreen);
        this.setState({ isFullScreen });
    }

    private isUserMember = (): boolean => {
        let isMember = true;
        if (isFeatureFlagEnabled(this.context.pageContext, SecurityConstants.PermissionFeatureFlag, false)) {
            isMember = this.context
                .pageContext
                .getService<IVssSecurityService>("IVssSecurityService")
                .hasPermission(
                    SecurityConstants.Namespace,
                    "",
                    SecurityConstants.ReadPermission);
            this.telemetryWriter.publish(SecurityConstants.PermissionTelemetry, { isPermissionIncluded: true });
        }

        return isMember;
    }

    private onDefaultSearchTextModified = (searchText: string): void => {
        this.updatePivotResultsCount(this.selectedTab.value, searchText);
    }

    private updatePivotResultsCount = (selectedTabId: string, searchText?: string): void => {
        if (searchText) {
            const inactiveEntities =
                this.tabProviders
                    .value
                    .filter(tab => tab.id !== selectedTabId && this.isCountRefreshNeeded(tab.id, searchText))
                    .map(tab => tab.id);

            this.source
                .getResultCounts(inactiveEntities, searchText)
                .then(countResponses => {
                    countResponses.forEach(countResponse => {
                        const pivotCount = this.pivotResultsCount[countResponse.id]
                        if (pivotCount) {
                            pivotCount.value = {
                                count: countResponse.count,
                                relationFromExactCount: countResponse.relationFromExactCount
                            }
                        }
                    })
                });
        }
    }

    private isCountRefreshNeeded = (tabId: string, text: string): boolean => {
        const state = this.pivotStateService.getState(tabId);
        if (state && state.countState) {
            return state.countState.searchtext !== text;
        }
        return true;
    }

    private onInputFocus = (): void => {
        // Log search box focus telemetry
        this.telemetryWriter.publish(TelemetryConstants.L2SearchBarFocused, { searchEntity: this.selectedTab.value });
    }

    private onInPageSearchStarted = (searchStatus: ISearchStatus): void => {
        if (!searchStatus || searchStatus.searchQuery.openInNewWindow) {
            return;
        }

        const { queryText, type } = searchStatus.searchQuery as ITfsSearchQuery;
        const searchText = queryText.trim();

        if (type !== this.selectedTab.value) { /* if tab is changed */
            this.onTabChanged(type, searchText);
        } else if (searchText !== this.defaultSearchText.value) { /* if searchText has changed */
            this.inputQuery.value = { text: searchText, openInNewTab: false };
            this.defaultSearchText.value = searchText;
        }
    }

    private resetViewOptions = (tabId: string, searchText: string): void => {
        const currentParams = this.historyService.getState();
        currentParams[SearchHubRouteConstants.EntityType] = tabId;
        currentParams[SearchHubRouteConstants.SearchText] = searchText;
        delete currentParams[SearchHubRouteConstants.LaunchPoint];
        delete currentParams[SearchHubRouteConstants.Result];
        delete currentParams[SearchHubRouteConstants.Filters];
        delete currentParams[SearchHubRouteConstants.SortOptions];
        delete currentParams[SearchHubRouteConstants.Action];
        delete currentParams[SearchHubRouteConstants.IncludeFacets];

        this.historyService.pushState(currentParams);
    }
}

VssComponent.register("searchHub", SearchHub);
